import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { formatCreatedDate } from '../../../utils/dates';
import { getStatusColors } from '../../../utils/statusColors';

interface CardProps {
    title?: string;
    subtitle?: string | null;
    headerRight?: React.ReactNode;
    maxHeight?: string;
    className?: string;
    children: React.ReactNode;
}

function Card({ title, subtitle, headerRight, maxHeight, className, children }: CardProps) {
    return (
        <div
            className={`rounded-xl border border-gray-200 bg-white p-5 flex flex-col ${maxHeight ?? ''} ${className ?? ''}`}
        >
            {(title || subtitle || headerRight) && (
                <div className="flex items-baseline justify-between mb-3 flex-shrink-0">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {title}
                    </h4>
                    {headerRight ??
                        (subtitle && <span className="text-xs text-gray-500">{subtitle}</span>)}
                </div>
            )}
            <div className="flex-1 min-h-0 flex flex-col">{children}</div>
        </div>
    );
}

function ValueField({
    label,
    value
}: {
    label: string;
    value: string | number | null | undefined;
}) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <span className="text-sm text-gray-800">{value ?? '--'}</span>
        </div>
    );
}

interface DotStyle {
    dot: string;
    text: string;
}

function DotValueField({
    label,
    value,
    styles,
    fallback = { dot: 'bg-gray-400', text: 'text-gray-800' }
}: {
    label: string;
    value: string | null | undefined;
    styles: Record<string, DotStyle>;
    fallback?: DotStyle;
}) {
    if (!value) {
        return <ValueField label={label} value={null} />;
    }
    const style = styles[value.toLowerCase()] ?? fallback;
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <span className={`flex items-center gap-1.5 text-sm font-medium ${style.text}`}>
                <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                {value}
            </span>
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string | null }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <span className="text-sm text-gray-800">{value ? formatCreatedDate(value) : '--'}</span>
        </div>
    );
}

interface StatusPillColorOverride {
    text: string;
    background: string;
}

interface StatusPillProps {
    value: string | null;
    className?: string;
    // Replaces the value-hashed Tailwind colour classes (getStatusColors) with
    // an explicit inline colour pair - used by callers whose colour comes from
    // a fixed lookup (e.g. PHASE_HEX) rather than a hash of the value itself.
    // Geometry (padding/radius/font/dot) never changes: only the colour
    // source does, so every StatusPill-based pill stays visually identical.
    colorOverride?: StatusPillColorOverride;
}

function StatusPill({ value, className = '', colorOverride }: StatusPillProps) {
    if (!value) {
        return (
            <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-gray-400 bg-gray-100 ${className}`}
            >
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                Not set
            </span>
        );
    }
    const colors = colorOverride ? null : getStatusColors(value);
    return (
        <Tooltip content={value}>
            <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${colors ? `${colors.text} ${colors.bg}` : ''} ${className}`}
                style={
                    colorOverride
                        ? { color: colorOverride.text, background: colorOverride.background }
                        : undefined
                }
            >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40 flex-shrink-0" />
                <span className="truncate">{value}</span>
            </span>
        </Tooltip>
    );
}

function StatusBadge({ label, value }: { label: string; value: string | null }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <StatusPill value={value} className="w-full px-3.5 py-2 text-[13px]" />
        </div>
    );
}

function safeHref(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:' ? value : undefined;
    } catch {
        return undefined;
    }
}

function LinkButton({ label, href: rawHref }: { label: string; href: string | null | undefined }) {
    const href = safeHref(rawHref);
    if (!href) {
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg cursor-not-allowed">
                {label}
                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            </span>
        );
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
        >
            {label}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
        </a>
    );
}

function NoteBlock({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                {value}
            </p>
        </div>
    );
}

export type { StatusPillColorOverride };
export {
    Card,
    ValueField,
    DateField,
    DotValueField,
    StatusPill,
    StatusBadge,
    LinkButton,
    NoteBlock
};
